import { tryPerformPlaybackRecoveryReload } from '@/helpers/playbackFailureReloadPolicy'

/** No successful playback-heartbeat POST while we expect one → WebView reload. */
export const PLAYBACK_HEARTBEAT_WATCHDOG_STALE_MS = 5 * 60_000
/** How often to check staleness (cheap; reload path is rate-limited separately). */
export const PLAYBACK_HEARTBEAT_WATCHDOG_POLL_MS = 30_000

let lastSuccessfulPlaybackHeartbeatAtMs = 0
let pollTimer: ReturnType<typeof setInterval> | null = null
let shouldExpectHeartbeat: (() => boolean) | null = null

/** Test-only reset. */
export function resetPlaybackHeartbeatWatchdogForTesting(): void {
  lastSuccessfulPlaybackHeartbeatAtMs = 0
  shouldExpectHeartbeat = null
  if (pollTimer != null) {
    clearInterval(pollTimer)
    pollTimer = null
  }
}

/** Call after a successful playback-heartbeat HTTP round-trip. */
export function notePlaybackHeartbeatSuccess(nowMs = Date.now()): void {
  lastSuccessfulPlaybackHeartbeatAtMs = nowMs
}

export function getLastSuccessfulPlaybackHeartbeatAtMsForTesting(): number {
  return lastSuccessfulPlaybackHeartbeatAtMs
}

/**
 * Arm a poll that reloads the WebView when heartbeats stop succeeding while still expected
 * (registered tenant, not quota-blocked). Shares recovery cooldown with {@link tryPerformPlaybackRecoveryReload}.
 */
export function armPlaybackHeartbeatWatchdog(opts: {
  shouldExpectHeartbeat: () => boolean
  nowMs?: number
  pollMs?: number
  staleMs?: number
  /** When false, only seeds/state is updated (unit tests that call {@link tickPlaybackHeartbeatWatchdog}). */
  startPoll?: boolean
}): void {
  shouldExpectHeartbeat = opts.shouldExpectHeartbeat
  const nowMs = opts.nowMs ?? Date.now()
  // Seed so cold start / first arm does not immediately trip.
  if (lastSuccessfulPlaybackHeartbeatAtMs === 0) {
    lastSuccessfulPlaybackHeartbeatAtMs = nowMs
  }
  if (opts.startPoll === false) {
    return
  }
  if (pollTimer != null) {
    return
  }
  const pollMs = opts.pollMs ?? PLAYBACK_HEARTBEAT_WATCHDOG_POLL_MS
  const staleMs = opts.staleMs ?? PLAYBACK_HEARTBEAT_WATCHDOG_STALE_MS
  pollTimer = setInterval(() => {
    tickPlaybackHeartbeatWatchdog({ nowMs: Date.now(), staleMs })
  }, pollMs)
}

export function clearPlaybackHeartbeatWatchdog(): void {
  shouldExpectHeartbeat = null
  // Drop the freshness baseline so the next arm reseeds — otherwise a long disarmed
  // gap (quota / unregister) looks instantly stale and trips recovery reload.
  lastSuccessfulPlaybackHeartbeatAtMs = 0
  if (pollTimer != null) {
    clearInterval(pollTimer)
    pollTimer = null
  }
}

/** Exposed for unit tests; also used by the interval. */
export function tickPlaybackHeartbeatWatchdog(opts: {
  nowMs: number
  staleMs?: number
}): boolean {
  const staleMs = opts.staleMs ?? PLAYBACK_HEARTBEAT_WATCHDOG_STALE_MS
  if (shouldExpectHeartbeat == null || !shouldExpectHeartbeat()) {
    return false
  }
  if (lastSuccessfulPlaybackHeartbeatAtMs <= 0) {
    return false
  }
  if (opts.nowMs - lastSuccessfulPlaybackHeartbeatAtMs < staleMs) {
    return false
  }
  return tryPerformPlaybackRecoveryReload(
    `heartbeat_watchdog_stale_${staleMs}ms`
  )
}
